import { Tooltip } from '@visx/tooltip';
import { CSSProperties, ReactNode } from 'react';
import { ChartPalette } from './chartStyle';
import { PlotArea } from './plotArea';

/** Which side of its anchor a tooltip extends to. */
export type TooltipLean = 'left' | 'right';

/** The gap between a tooltip's anchor and its nearer edge, in pixels. */
const ANCHOR_GAP_PX = 12;

/**
 * A chart tooltip drawn inside the SVG, over the plot, anchored at a pixel
 * of the chart: it extends to the right of the anchor when leaning right,
 * to the left when leaning left, and hangs below the anchor unless flipped
 * up. Its position is an inline style, so a move costs no stylesheet work,
 * and the style given here replaces the visx tooltip's own defaults. It
 * takes no pointer events, so the plot under it keeps the hover.
 */
export default function ChartTooltip({
    plot,
    palette,
    anchor,
    lean,
    flipUp = false,
    style,
    children,
    'data-testid': testId,
}: {
    plot: PlotArea;
    palette: ChartPalette;
    anchor: { x: number; y: number };
    lean: TooltipLean;
    flipUp?: boolean;
    style?: CSSProperties;
    children: ReactNode;
    'data-testid'?: string;
}) {
    const placement =
        lean === 'right'
            ? { left: anchor.x + ANCHOR_GAP_PX }
            : { right: `calc(100% - ${anchor.x - ANCHOR_GAP_PX}px)` };
    return (
        <foreignObject
            x={0}
            y={0}
            width={plot.left + plot.width}
            height={plot.top + plot.height}
            pointerEvents="none"
            style={{ overflow: 'visible' }}
        >
            <Tooltip
                data-testid={testId}
                applyPositionStyle={false}
                style={{
                    position: 'absolute',
                    top: anchor.y,
                    ...placement,
                    transform: flipUp ? 'translateY(-100%)' : undefined,
                    pointerEvents: 'none',
                    background: palette.tooltipBackground,
                    border: `1px solid ${palette.tooltipBorder}`,
                    color: palette.tooltipText,
                    borderRadius: 4,
                    zIndex: 1,
                    ...style,
                }}
            >
                {children}
            </Tooltip>
        </foreignObject>
    );
}
